import React, { useState } from "react";

const PrivacySettings = () => {
  const [loading, setLoading] = useState({
    dataRequest: false,
    deletionRequest: false,
    export: false,
  });
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null); // 'success' or 'error'

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage(null);
      setMessageType(null);
    }, 5000);
  };

  const handleRequestData = async () => {
    try {
      setLoading({ ...loading, dataRequest: true });

      const response = await fetch("/api/settings/request-data", {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        showMessage(
          "Your data access request has been received. You will hear back from us shortly.",
          "success"
        );
      } else {
        showMessage(data.error || "Failed to submit request", "error");
      }
    } catch (err) {
      showMessage("Failed to submit request. Please try again.", "error");
      console.error("Request data error:", err);
    } finally {
      setLoading({ ...loading, dataRequest: false });
    }
  };

  const handleRequestDeletion = async () => {
    try {
      setLoading({ ...loading, deletionRequest: true });

      const response = await fetch("/api/settings/request-deletion", {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        showMessage(
          "Your account deletion request has been received. We will process your request within 30 days as required by GDPR.",
          "success"
        );
      } else {
        showMessage(data.error || "Failed to submit request", "error");
      }
    } catch (err) {
      showMessage("Failed to submit request. Please try again.", "error");
      console.error("Request deletion error:", err);
    } finally {
      setLoading({ ...loading, deletionRequest: false });
    }
  };

  const handleExportData = async () => {
    try {
      setLoading({ ...loading, export: true });

      const response = await fetch("/api/settings/export-data", {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        showMessage(
          "Your data export is being processed. You will receive an email with your complete data shortly.",
          "success"
        );
      } else {
        showMessage(data.error || "Failed to export data", "error");
      }
    } catch (err) {
      showMessage("Failed to export data. Please try again.", "error");
      console.error("Export data error:", err);
    } finally {
      setLoading({ ...loading, export: false });
    }
  };

  return (
    <div className="settings-content">
      <div className="settings-section">
        <div className="section-header">
          <h2 className="section-title">Privacy & Security</h2>
          <p className="section-subtitle">Your privacy and data protection</p>
        </div>

        {message && (
          <div
            className={`alert alert-${messageType}`}
            style={{
              padding: "10px",
              marginBottom: "20px",
              backgroundColor: messageType === "success" ? "#efe" : "#fee",
              color: messageType === "success" ? "#3c3" : "#c33",
              borderRadius: "5px",
            }}
          >
            {message}
          </div>
        )}

        <div className="settings-card">
          <div className="card-header">
            <h3>Data Protection</h3>
          </div>

          <div className="privacy-content">
            <div className="privacy-item">
              <h4>🔒 We Protect Your Data</h4>
              <p>
                We will never sell, rent, or share your personal information
                with third parties without your explicit consent. Your data is
                encrypted and stored securely.
              </p>
            </div>

            <div className="privacy-item">
              <h4>📧 Communication</h4>
              <p>
                We only use your contact information to send you important
                updates about your account and bookings. You can opt out of
                promotional emails at any time.
              </p>
            </div>

            <div className="privacy-item">
              <h4>💳 Payment Information</h4>
              <p>
                All payment processing is handled by secure, PCI-compliant
                third-party services. We never store your credit card
                information on our servers.
              </p>
            </div>

            <div className="privacy-item">
              <h4>🎵 Artist Information</h4>
              <p>
                Artist profiles and booking information are only shared with
                venues you choose to work with. You control who sees your
                information.
              </p>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <h3>Your Rights (GDPR Compliance)</h3>
          </div>

          <div className="rights-content">
            <div className="right-item">
              <div className="right-info">
                <h4>📋 Data Access</h4>
                <p>
                  You have the right to request a copy of all data we have about
                  you. We will provide your complete data within 30 days.
                </p>
              </div>
              <button
                className="action-btn"
                onClick={handleRequestData}
                disabled={loading.dataRequest}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                {loading.dataRequest ? "Processing..." : "Request Data"}
              </button>
            </div>

            <div className="right-item">
              <div className="right-info">
                <h4>✏️ Data Correction</h4>
                <p>
                  You can update or correct your information at any time through
                  your profile settings.
                </p>
              </div>
            </div>

            <div className="right-item">
              <div className="right-info">
                <h4>🗑️ Data Deletion Request</h4>
                <p>
                  You can request complete deletion of your account and all
                  associated data. We will process your request within 30 days
                  as required by GDPR.
                </p>
              </div>
              <button
                className="action-btn"
                onClick={handleRequestDeletion}
                disabled={loading.deletionRequest}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#c33",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                {loading.deletionRequest ? "Processing..." : "Request Deletion"}
              </button>
            </div>

            <div className="right-item">
              <div className="right-info">
                <h4>📤 Data Portability</h4>
                <p>
                  You can export your data in a standard format (JSON) for use
                  with other services. Your data will be emailed to you.
                </p>
              </div>
              <button
                className="action-btn"
                onClick={handleExportData}
                disabled={loading.export}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                {loading.export ? "Exporting..." : "Export Data"}
              </button>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <h3>Cookies & Tracking</h3>
          </div>

          <div className="cookie-content">
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Keep you logged in to your account</li>
              <li>Remember your preferences</li>
              <li>Analyze how our service is used (anonymously)</li>
              <li>Improve our service performance</li>
            </ul>

            <p>
              You can control cookie settings through your browser preferences.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;
